import {useMemo} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {get} from '@cullylarson/f'
import {preventDefault} from '@@client/lib/events'
import {formatDollars} from '@@client/lib/numbers'
import {useTokenCache} from '@@client/lib/hooks'
import {useCart} from '@@client/cart/cart-hooks'
import {getItemNames} from '@@client/cart/cart-repo'
import loadLocationList from '@@client/location/loadLocationList'
import loadStorePickupOptions from './loadStorePickupOptions'
import loadCanHomeShip from './loadCanHomeShip'
import {preferredStateOrder, groupLocationsByStateOrdered} from '@@client/location/location-helper'
import {
    SHIPPING_METHOD_IN_STORE,
    SHIPPING_METHOD_HOME,
    getStorePickupPriceFromId,
} from './checkout-utils'
import {bigBlueStyle} from '@@client/checkout/checkout-styles'
import {
    setCheckoutField,
    setShippingMethod,
    getHomeShippingEstimate,
    choosePickupStore,
    getSalesTax,
    getStorePickup,
} from './actions'
import Loading from '@@client/components/Loading'
import FormText from '@@client/components/FormText'
import FormUSStateSelect from '@@client/components/FormUSStateSelect'
import FormCheckboxList from '@@client/components/FormCheckboxList'
import Messages from '@@client/components/Messages'
import StoreListSimple from '@@client/location/StoreListSimple'
import StepSummaryItem from './StepSummaryItem'
import NextStepButton from './NextStepButton'

const InStoreShippingForm = ({onSubmit, errors, paramErrors, disabled = false}) => {
    loadLocationList()
    loadStorePickupOptions()

    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info
    const storePickupOptions = checkoutStore.storePickupOptions
    const cart = useCart()
    const locationStore = useSelector(state => state.location)

    // if the pickup options have restricted the stores that can be will-called (i.e. because some parts can't be intra-store will-called), then we only want to show those locations
    const locations = useMemo(() => {
        // if the storePickupOptions.result is null, then we haven't fetched the options and we shouldn't show any locations
        if(storePickupOptions.result === null) {
            return []
        }
        else {
            const willCallStoreIds = storePickupOptions.result.willCallStores.map(x => x.storeId)
            return locationStore.list.entities.filter(x => willCallStoreIds.includes(x.id))
        }
    }, [locationStore.list.entities, storePickupOptions.result?.willCallStores])

    const locationsGrouped = useMemo(() => groupLocationsByStateOrdered(preferredStateOrder, locations), [locations])

    const getStoreName = (storeEntity, includeState = false) => {
        if(storeEntity.name) return storeEntity.name

        return includeState
            ? `${storeEntity.city}, ${storeEntity.state}`
            : storeEntity.city
    }

    const handlePickupStoreIdChanged = storeId => {
        dispatch(choosePickupStore(storeId))
    }

    const handleSubmit = preventDefault(() => {
        dispatch(getStorePickup(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    })

    const getStorePickupPriceFormattedFromId = (checkoutStore, storeId) => {
        const willCallFee = getStorePickupPriceFromId(checkoutStore, storeId)

        return willCallFee === 0
            ? 'FREE'
            : formatDollars(willCallFee, {includeZeroDecimal: false})
    }

    const renderStoreList = () => {
        if(storePickupOptions.doing) {
            return <Loading />
        }

        if(storePickupOptions.errors.length !== 0) {
            return null
        }

        if(storePickupOptions.result !== null && storePickupOptions.result.canIntraStoreWillCall === false) {
            return null
        }

        return (
            <StoreListSimple
                locationsGrouped={locationsGrouped}
                highlightedLocationId={checkoutInfo.pickupStoreId}
                onLocationClick={storeInfo => handlePickupStoreIdChanged(storeInfo.id)}
                getStoreName={storeInfo => (<span>{getStoreName(storeInfo)} &mdash; <em>{getStorePickupPriceFormattedFromId(checkoutStore, storeInfo.id)}</em></span>)}
            />
        )
    }

    const renderNextButton = () => {
        if(storePickupOptions.doing) {
            return null
        }

        if(storePickupOptions.errors.length !== 0) {
            return null
        }

        if(storePickupOptions.result !== null && storePickupOptions.result.canIntraStoreWillCall === false) {
            return null
        }

        return (
            <form onSubmit={handleSubmit}>
                <NextStepButton disabled={disabled}>Next: Payment Info</NextStepButton>
            </form>
        )
    }

    const renderIntraStoreRestrictionMessage = () => {
        if(storePickupOptions.result === null) {
            return null
        }

        if(storePickupOptions.result.reasonMessage === null) {
            return null
        }

        const offendingItemIds = storePickupOptions.result.reasonInventoryIds || []
        const offendingItems = getItemNames(cart, offendingItemIds).join(', ')
        const message = offendingItemIds.length
            ? `${storePickupOptions.result.reasonMessage} (${offendingItems})`
            : storePickupOptions.result.reasonMessage

        return <Messages notice={[message]} />
    }

    return (
        <>
            <p className={bigBlueStyle.className}>Pick up your order at a store near you:</p>

            <Messages error={errors} />
            <Messages error={get('pickupStoreId', [], paramErrors)} />

            {renderIntraStoreRestrictionMessage()}

            <div className='store-list'>
                {renderStoreList()}
            </div>
            {renderNextButton()}
            {bigBlueStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .store-list {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        grid-gap: 30px;

                        @media(--xxxs) {
                            display: block;
                        }
                    }
                `}
            </style>
        </>
    )
}

const HomeShippingForm = ({
    onSubmit,
    errors,
    paramErrors,
    disabled = false,
}) => {
    loadCanHomeShip()

    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info
    const cart = useCart()

    const idPrefix = 'checkout-shipping-home'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    const handleSubmit = preventDefault(() => {
        dispatch(getHomeShippingEstimate(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    })

    if(checkoutStore.canHomeShip.doing) {
        return <Loading />
    }

    if(checkoutStore.canHomeShip.errors.length) {
        return <Messages error={checkoutStore.canHomeShip.errors} />
    }

    if(checkoutStore.canHomeShip.canHomeShip === false) {
        const itemNames = getItemNames(cart, checkoutStore.canHomeShip.willCallOnlyInventoryIds).join(', ')
        return <Messages notice={[`Some of your cart items cannot be shipped. They must be picked up at a B&R store location. (${itemNames})`]} />
    }

    return (
        <>
            <Messages error={errors} />

            <form onSubmit={handleSubmit}>
                <div className='field-sets'>
                    <div className='field-set-item'>
                        <FormText
                            idPrefix={idPrefix}
                            name='shippingName'
                            title='Name'
                            value={checkoutInfo.shippingName || ''}
                            onChange={handleParam('shippingName')}
                            errors={get('shippingName', [], paramErrors)}
                            autoComplete='name'
                        />
                    </div>
                    <div className='field-set-item'>
                        <FormText
                            idPrefix={idPrefix}
                            name='shippingCompany'
                            title='Company'
                            value={checkoutInfo.shippingCompany || ''}
                            onChange={handleParam('shippingCompany')}
                            errors={get('shippingCompany', [], paramErrors)}
                            autoComplete='organization'
                        />
                    </div>
                </div>

                <div className='field-sets'>
                    <div className='field-set-item'>
                        <FormText
                            idPrefix={idPrefix}
                            name='shippingAddress1'
                            title='Address Line 1'
                            value={checkoutInfo.shippingAddress1 || ''}
                            onChange={handleParam('shippingAddress1')}
                            errors={get('shippingAddress1', [], paramErrors)}
                            required
                            autoComplete='address-line1'
                        />

                        <FormText
                            idPrefix={idPrefix}
                            name='shippingAddress2'
                            title='Address Line 2'
                            value={checkoutInfo.shippingAddress2 || ''}
                            onChange={handleParam('shippingAddress2')}
                            errors={get('shippingAddress2', [], paramErrors)}
                            autoComplete='address-line2'
                        />
                    </div>
                    <div className='field-set-item'>
                        <FormText
                            idPrefix={idPrefix}
                            name='shippingCity'
                            title='City'
                            value={checkoutInfo.shippingCity || ''}
                            onChange={handleParam('shippingCity')}
                            errors={get('shippingCity', [], paramErrors)}
                            required
                            autoComplete='address-level2'
                        />

                        <div className='state-zip'>
                            <FormUSStateSelect
                                name='shippingState'
                                title='State'
                                value={checkoutInfo.shippingState}
                                onChange={handleParam('shippingState')}
                                errors={get('shippingState', [], paramErrors)}
                                required
                                autoComplete='address-level1'
                            />

                            <FormText
                                idPrefix={idPrefix}
                                name='shippingZip'
                                title='Zip'
                                value={checkoutInfo.shippingZip || ''}
                                onChange={handleParam('shippingZip')}
                                errors={get('shippingZip', [], paramErrors)}
                                required
                                autoComplete='postal-code'
                            />
                        </div>
                    </div>
                </div>

                <NextStepButton disabled={disabled}>Next: Payment Info</NextStepButton>
            </form>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .field-sets {
                        @media(--xl) {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            grid-gap: 30px;
                        }
                    }

                    .field-set-item {
                        max-width: 350px;
                        margin: 0 auto;

                        @media(--xl) {
                            margin: 0;
                            max-width: none;
                        }
                    }

                    .state-zip {
                        display: grid;
                        grid-template-columns: 2fr 1fr;
                        grid-gap: 10px;
                    }
                `}
            </style>
        </>
    )
}

const ShippingMethodForm = ({disabled = false}) => {
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const idPrefix = 'checkout-shipping-method'

    const handleShippingMethodChange = e => (e && e.target) ? dispatch(setShippingMethod(e.target.value)) : dispatch(setShippingMethod(e))

    const options = [
        {label: 'Ship to Your Location', value: SHIPPING_METHOD_HOME},
        {label: 'In-Store Pickup', value: SHIPPING_METHOD_IN_STORE},
    ]

    return (
        <>
            <div className='container'>
                <h4>Select Shipping Method</h4>

                <form>
                    <FormCheckboxList
                        kind='radio'
                        idPrefix={idPrefix}
                        name='shipping-method'
                        onChange={handleShippingMethodChange}
                        selectedValue={checkoutInfo.shippingMethod}
                        options={options}
                        disabled={disabled}
                    />
                </form>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        margin-bottom: 15px;
                        border-bottom: 1px solid $c--blue--light-gray;
                    }

                    h4 {
                        margin-bottom: 12px;
                    }

                    form :global(.field-input) {
                        display: flex;
                    }

                    form :global(.field-checkbox-item) {
                        &:first-child {
                            margin-right: 20px;
                        }
                    }

                    form :global(label) {
                        font-style: italic;
                        margin-left: 5px;
                    }

                    form :global(.field-checkbox-item) {
                        margin-bottom: 0;
                    }
                `}
            </style>
        </>
    )
}

export default function StepShipping({showSummary, onSubmit, disabled = false}) {
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const shippingMethod = checkoutStore.info.shippingMethod || SHIPPING_METHOD_HOME

    const handleSubmit = () => {
        dispatch(getSalesTax(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    }

    if(showSummary) {
        if(shippingMethod === SHIPPING_METHOD_IN_STORE) {
            return (
                <StepSummaryItem title='In-Store Pickup'>{checkoutInfo.pickupStoreName}</StepSummaryItem>
            )
        }
        else {
            return (
                <StepSummaryItem title='Ship to Your Location'>
                    {checkoutInfo.shippingName}<br />
                    {checkoutInfo.shippingCompany ? <span>{checkoutInfo.shippingCompany}<br /></span> : null}
                    {checkoutInfo.shippingAddress1}<br />
                    {checkoutInfo.shippingAddress2 ? <span>{checkoutInfo.shippingAddress2}<br /></span> : null}
                    {checkoutInfo.shippingCity}, {checkoutInfo.shippingState} {checkoutInfo.shippingZip}
                </StepSummaryItem>
            )
        }
    }
    else {
        return (
            <div className='step-shipping'>
                <Messages error={checkoutStore.salesTax.errors} />

                <ShippingMethodForm disabled={disabled} />

                {
                    shippingMethod === SHIPPING_METHOD_IN_STORE
                        ? <InStoreShippingForm onSubmit={handleSubmit} disabled={disabled} errors={checkoutStore.storePickupOptions.errors.concat(checkoutStore.storePickup.errors)} paramErrors={checkoutStore.storePickup.paramErrors} />
                        : <HomeShippingForm onSubmit={handleSubmit} disabled={disabled} errors={checkoutStore.homeShippingEstimate.errors} paramErrors={checkoutStore.homeShippingEstimate.paramErrors} />
                }
            </div>
        )
    }
}
